import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Card, CardBody, CardHeader, Button } from 'reactstrap';
import { getEntregasPorIp } from '../../../api/reportApi';
import TableContainer from '../../../components/TableContainerReactTable';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';

interface DeliveryByIpCardProps {
  campaignId: string;
}

export interface DeliveryByIpCardRef {
  refresh: () => void;
}

const DeliveryByIpCard = forwardRef<DeliveryByIpCardRef, DeliveryByIpCardProps>(
  ({ campaignId }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasLoadedInitially, setHasLoadedInitially] = useState(false);
    numeral.locale('pt-br');
    const numeralInteger = '0,0';

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const ipData = await getEntregasPorIp(parseInt(campaignId));
        setData(ipData);
        setHasLoadedInitially(true);
      } catch (err) {
        console.error('Error fetching IP delivery data:', err);
        setError('Erro ao carregar dados de entregas por IP');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      // Remove auto-fetch on mount - data will only be loaded when user clicks button
    }, []);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    // Show initial state with generate button if data hasn't been loaded yet
    if (!hasLoadedInitially && !loading) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Entregas por IP</h4>
          </CardHeader>
          <CardBody>
            <div className="text-center py-5">
              <div className="mb-4">
                <i className="ri-earth-line text-muted" style={{ fontSize: '3rem' }}></i>
              </div>
              <h5 className="text-muted mb-3">Relatório de Entregas por IP</h5>
              <p className="text-muted mb-4">
                Clique no botão abaixo para gerar o relatório de entregas por endereço IP.
                Isso ajuda a reduzir a carga no banco de dados.
              </p>
              <Button
                color="primary"
                onClick={fetchData}
                disabled={loading}
                className="px-4"
              >
                <i className="ri-bar-chart-line me-2"></i>
                Gerar Relatório
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    }

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Entregas por IP</h4>
          </CardHeader>
          <CardBody>
            <div className="text-center text-danger">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </div>
          </CardBody>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Entregas por IP</h4>
          </CardHeader>
          <CardBody>
            <div className="text-center text-muted">
              <i className="ri-information-line me-2"></i>
              Nenhuma entrega por IP encontrada
            </div>
          </CardBody>
        </Card>
      );
    }

    // Column definitions for the table
    const columns = [
      {
        header: 'Endereço IP',
        accessorKey: 'ip',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        id: 'impression',
        header: 'Entregues',
        accessorFn: (row: any) => (row.impression || 0) + (row.start || 0),
        cell: (row: any) => numeral(row.getValue() || 0).format(numeralInteger),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        id: 'viewable',
        header: 'Visíveis',
        accessorKey: 'viewable',
        cell: (row: any) => numeral(row.getValue() || 0).format(numeralInteger),
        enableColumnFilter: false,
        enableSorting: true,
      },
    ];

    return (
      <Card className={styles.reportCard}>
        <CardHeader className={styles.cardHeader}>
          <h4 className={styles.cardTitle}>Entregas por IP</h4>
        </CardHeader>
        <CardBody>
          <TableContainer
            divClass="table-responsive"
            columns={columns}
            data={data}
            isGlobalFilter={false}
            customPageSize={10}
            initialSorting={[{ id: 'impression', desc: true }]}
          />
        </CardBody>
      </Card>
    );
  }
);

DeliveryByIpCard.displayName = 'DeliveryByIpCard';

export default DeliveryByIpCard;
